<ng-template #entityLogo>
  @if (isDashboard && isSettlement) {
    <div class="entity-logo dashboard">
      <img src="shared-assets/icons/merchant.svg" alt="Dashboard Logo" class="logo-img dashboard-logo" />
    </div>
  } @else {
    <div class="entity-logo">
      @if (showLogoSkeleton) {
        <ngx-skeleton-loader
          aria-hidden="true"
          appearance="line"
          [theme]="{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            margin: '0',
          }"
          >
        </ngx-skeleton-loader>
      } @else {
        @if (networkIcon) {
          <img
            [src]="networkIcon"
            (error)="useFallbackIcon($event)"
            alt="Network Logo"
            class="logo-img merchant-logo"
            [ngClass]="{ 'inactive-logo': !isActive }"
            />
        } @else {
          @if (logoBase64) {
            <img [src]="'data:image/png;base64,' + logoBase64" alt="Entity Logo" class="logo-img merchant-logo" />
          } @else {
            <span class="logo-text">{{ logoText }}</span>
          }
        }
      }
    </div>
  }
</ng-template>

@if (layout === 'dashboard') {
  <div class="financial-summary-header card dashboard-layout">
    <div class="dashboard-header-content">
      <div class="dashboard-identity">
        <ng-container *ngTemplateOutlet="entityLogo" />
        @if (isLoading && !entityName) {
          <ngx-skeleton-loader
            ariaLabel="Loading name"
            appearance="line"
            [theme]="{ width: '140px', height: '27px', borderRadius: '6px', margin: '0' }"
            >
          </ngx-skeleton-loader>
        } @else {
          <h2 class="dashboard-entity-name">{{ entityName }}</h2>
        }
      </div>

      <div class="dashboard-divider"></div>

      <div class="dashboard-balance">
        <span class="balance-label">{{ balanceLabel }}</span>
        <p class="entity-balance olv-figure">
          @if (!isLoading && balanceError) {
            <span class="balance-int balance-unavailable" [attr.title]="balanceError">—</span>
          } @else if (!isLoading) {
            @if (
              entityData.walletBalances?.totalBalanceInBaseCurrency
              | splitParts
              : entityData.walletBalances?.baseCurrencyDisplayPrecision
              : entityData.walletBalances?.baseCurrency;
              as parts
              ) {
              <span class="balance-int">{{ parts.integer }}</span>
              @if (parts.decimal) {
                <span class="balance-decimal">{{ parts.decimal }}</span>
              }
            }
          } @else {
            <ngx-skeleton-loader
              ariaLabel="Loading balance"
              appearance="line"
              [theme]="{ width: '150px', height: '33px', borderRadius: '8px', margin: '0' }"
              >
            </ngx-skeleton-loader>
          }
        </p>
      </div>

      <div class="dashboard-divider"></div>

      <div class="dashboard-actions">
        @if (isDashboard) {
          @if (!isLoading && !isUpdatingSweeping) {
            <ng-container
              *appHasPermission="{
                resource: Resources.Dashboard,
                scope: isSweepingEnabled ? Scopes.DisableSweeping : Scopes.EnableSweeping,
              }"
              >
              <button
                type="button"
                class="sweeping-status-btn"
                [ngClass]="isSweepingEnabled ? 'disable-btn' : 'activate-btn'"
                [attr.aria-label]="isSweepingEnabled ? 'Disable sweeping' : 'Enable sweeping'"
                [matTooltip]="isSweepingEnabled ? 'Disable sweeping' : 'Enable sweeping'"
                matTooltipPosition="above"
                (click)="sweepingStatusUpdate()"
                >
                <mat-icon class="sweeping-icon">cleaning_services</mat-icon>
                <p class="btn-label">
                  {{ isSweepingEnabled ? 'Disable sweeping' : 'Enable sweeping' }}
                </p>
              </button>
            </ng-container>
          } @else {
            <ngx-skeleton-loader
              ariaLabel="Loading sweeping status"
              appearance="line"
              [theme]="{ width: '160px', height: '32px', borderRadius: '22px', margin: '0' }"
              >
            </ngx-skeleton-loader>
          }
        }

        @if (showAssetsButton) {
          <button
            type="button"
            class="show-assets-btn"
            [class.open]="assetsOpen"
            [attr.aria-expanded]="assetsOpen"
            [attr.aria-label]="assetsOpen ? 'Hide assets' : 'Show assets'"
            [matTooltip]="assetsOpen ? 'Hide assets' : 'Show assets'"
            matTooltipPosition="above"
            aria-haspopup="dialog"
            (click)="toggleAssets.emit()"
            >
            <mat-icon class="show-assets-icon">{{ assetsOpen ? 'visibility_off' : 'visibility' }}</mat-icon>
            <span class="btn-label">{{ assetsOpen ? 'Hide assets' : 'Show assets' }}</span>
          </button>
        }
      </div>
    </div>
  </div>
} @else {
  <div
    class="financial-summary-header card"
    [class.has-lifecycle]="hasHeaderCards"
    >
    <div class="financial-summary-header-content">
      <div class="entity-info">
        <ng-container *ngTemplateOutlet="entityLogo" />

        <div class="entity-details">
          @if (isLoading) {
            <div class="entity-details-skeleton">
              @if (isWalletDetails || !isDashboard) {
                <ngx-skeleton-loader
                  ariaLabel="Loading details"
                  appearance="line"
                  [theme]="{
                    width: '180px',
                    height: '22px',
                    borderRadius: '6px',
                    marginBottom: '0',
                  }"
                  >
                </ngx-skeleton-loader>
              }
              @if (isWalletDetails) {
                <ngx-skeleton-loader
                  aria-hidden="true"
                  appearance="line"
                  [theme]="{
                    width: '340px',
                    maxWidth: '100%',
                    height: '18px',
                    borderRadius: '6px',
                    marginBottom: '0',
                  }"
                  >
                </ngx-skeleton-loader>
              }
            </div>
          } @else {
            <h2 class="entity-name" [ngClass]="{ 'text-black': entityAddress }">
              {{ entityName }}
              @if (!isActive && isWalletDetails) {
                <span class="dot-separator">•</span>
                <span class="disabled-text">Disabled</span>
              }
              @if (isWalletDetails) {
                <span
                  class="risk-badge"
                  [matTooltip]="riskToolTip(risk)"
                  matTooltipPosition="above"
                  >
                  <span class="warning-icon" [ngClass]="'risk-' + (risk || 'not-assessed')"></span>
                </span>
              }
              @if (isWalletDetails && showLifecycleBadge) {
                <span
                  class="lifecycle-badge"
                  [ngClass]="isServing ? 'lifecycle-serving' : 'lifecycle-' + lifecycleBadge"
                  [matTooltip]="isServing ? 'This wallet is currently serving withdrawals' : ''"
                  matTooltipPosition="above"
                  >
                  <span class="lifecycle-text">{{ lifecycleText }}</span>
                  @if (isServing && (lifecycleBadge === 'active' || lifecycleBadge === 'capped')) {
                    <span class="lifecycle-dot" [ngClass]="'dot-' + lifecycleBadge"></span>
                  }
                </span>
              }
            </h2>

            @if (entityAddress) {
              <h2 class="entity-address olv-address">
                <span class="full-address">{{ entityAddress }}</span>
                <span class="short-address" [matTooltip]="entityAddress" matTooltipPosition="above">{{
                  shortenAddress(entityAddress)
                }}</span>
                <span [matTooltip]="tooltipText" matTooltipPosition="above">
                  <img
                    src="shared-assets/icons/copy.svg"
                    alt="Copy address"
                    class="copy-icon"
                    (click)="copyToClipboard(entityAddress)"
                    />
                </span>
              </h2>
            }
          }

          <div class="balance-container">
            <p class="entity-balance olv-figure">
              @if (!isLoading) {
                @if (
                  entityData.walletBalances?.availableBalanceInBaseCurrency
                  | splitParts
                  : entityData.walletBalances?.baseCurrencyDisplayPrecision
                  : entityData.walletBalances?.baseCurrency;
                  as parts
                  ) {
                  <span class="balance-int">{{ parts.integer }}</span>
                  @if (parts.decimal) {
                    <span class="balance-decimal">{{ parts.decimal }}</span>
                  }
                }
              } @else {
                <ngx-skeleton-loader
                  ariaLabel="Loading balance"
                  appearance="line"
                  [theme]="{ width: '150px', height: '30px', borderRadius: '8px', margin: '0' }"
                  >
                </ngx-skeleton-loader>
              }
            </p>
            @if (!isLoading && this.isWalletDetails) {
              <button
                type="button"
                *appHasPermission="{ resource: walletResource, scope: Scopes.Sweep }"
                [matTooltip]="sweepTooltipText"
                matTooltipPosition="above"
                [disabled]="isSweepDisabled"
                (click)="toggleSweep()"
                >
                Sweep
              </button>
              @if (canMoveNonSpendableFunds) {
                <button
                  type="button"
                  *appHasPermission="{ resource: walletResource, scope: Scopes.Sweep }"
                  matTooltip="Move unspent gas funding to the burn wallet"
                  matTooltipPosition="above"
                  (click)="moveNonSpendableFunds()"
                  >
                  Move Non-Spendable
                </button>
              }
            }
          </div>
        </div>
      </div>

      @if (!isLoading) {
        <div class="currency-balances">
          @for (balance of balances; track balance.currency; let i = $index) {
            <div class="currency-card" (mouseenter)="showRiskTooltip(i, $event)" (mouseleave)="hideRiskTooltip()">
              <div class="currency-icon-container">
                @if (balance.icons.length > 1) {
                  @for (icon of balance.icons; track $index; let iconIndex = $index) {
                    <img
                      [src]="icon"
                      (error)="useFallbackIcon($event)"
                      alt=""
                      class="icon"
                      [ngClass]="{
                        'main-icon': iconIndex === 0,
                        'secondary-icon': iconIndex === 1,
                      }"
                      />
                  }
                } @else {
                  @for (icon of balance.icons; track $index) {
                    <img [src]="icon" (error)="useFallbackIcon($event)" alt="" class="icon" />
                  }
                }

                <span class="currency-icon" [ngClass]="balance.iconClass"></span>
              </div>

              @if (balance.amount | splitParts: balance.precision; as parts) {
                <div class="currency-details">
                  <div class="currency-label">{{ balance.currency }}</div>
                  <div class="currency-value olv-figure">
                    <span class="currency-int">{{ parts.integer }}</span>
                    @if (parts.decimal) {
                      <span class="currency-decimal">{{ parts.decimal }}</span>
                    }
                  </div>
                </div>
                @if (hoveredCurrencyIndex === i) {
                  <div
                    class="custom-risk-tooltip"
                    [style.left.px]="tooltipLeft"
                    (mouseenter)="keepRiskTooltipOpen(i)"
                    (mouseleave)="hideRiskTooltip()"
                    >
                    @if (entityData.walletBalances?.currencyBalances?.[i]; as currency) {
                      <app-risk-distribution-tooltip
                        [currency]="currency"
                        [baseCurrencyDisplayPrecision]="entityData.walletBalances.baseCurrencyDisplayPrecision"
                        >
                      </app-risk-distribution-tooltip>
                    }
                  </div>
                }
              }
            </div>
          }
        </div>
      } @else {
        <div class="currency-balances">
          @for (_ of isWalletDetails || !isSettlement ? [1, 2] : [1, 2, 3, 4]; track $index) {
            <div class="currency-card" style="width: 133px">
              <div class="currency-icon-container">
                <ngx-skeleton-loader
                  aria-hidden="true"
                  appearance="line"
                  [theme]="{
                    width: '43px',
                    height: '43px',
                    borderRadius: '5px',
                    margin: '0',
                  }"
                  >
                </ngx-skeleton-loader>
              </div>

              <div class="currency-details">
                <ngx-skeleton-loader
                  aria-hidden="true"
                  appearance="line"
                  [theme]="{
                    width: '60px',
                    height: '15px',
                    borderRadius: '6px',
                    marginBottom: '0',
                  }"
                  >
                </ngx-skeleton-loader>

                <ngx-skeleton-loader
                  ariaLabel="Loading currency balances"
                  [attr.aria-hidden]="!$first || null"
                  appearance="line"
                  [theme]="{
                    width: '85px',
                    height: '21px',
                    borderRadius: '6px',
                    margin: '0',
                  }"
                  >
                </ngx-skeleton-loader>
              </div>
            </div>
          }
        </div>
      }
    </div>

    @if (hasHeaderCards) {
      <div class="lifecycle-divider"></div>
      @if (lifecycleCards.length) {
        <div class="lifecycle-cards">
          @for (card of lifecycleCards; track card.title) {
            <app-info-card
              [variant]="card.variant"
              [title]="card.title"
              [value]="card.value ?? ''"
              [progress]="card.progress ?? 0"
              [progressLabel]="card.progressLabel ?? ''"
              >
            </app-info-card>
          }
        </div>
      }
      @if (delegatedCards.length) {
        <div class="lifecycle-cards delegated-cards" data-testid="delegated-resources">
          @for (card of delegatedCards; track card.title) {
            <app-info-card
              [variant]="card.variant"
              [title]="card.title"
              [value]="card.value ?? ''"
              [progress]="card.progress ?? 0"
              [progressLabel]="card.progressLabel ?? ''"
              >
            </app-info-card>
          }
        </div>
      }
    }
  </div>
}
